import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import type { PlatformService } from '@/api/types';
import { Callout } from '@/components/Callout';
import { StatTile } from '@/components/StatTile';
import { formatWhen } from './AccountsPage';

/** A container restarting more than this is treated as a problem even while "running". */
const RESTART_THRESHOLD = 3;

/**
 * Platform service inventory — every container across every stack.
 *
 * Deliberately shows RESTART COUNT next to state. A crash-looping container reports "running"
 * between restarts, so a status-only view calls it healthy; the restart count is what actually
 * surfaces it. On the live estate this is what exposed Kafka restarting five times while
 * reporting as up.
 */
export function ServicesPage() {
  const [onlyProblems, setOnlyProblems] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['services'],
    queryFn: api.services,
    // Pushed in by the collector rather than polled, so refetching often buys nothing.
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading) return <div className="empty-state"><span className="spinner" /></div>;

  if (isError) {
    return (
      <Callout tone="error" title="Services unavailable">
        {error instanceof ApiError ? error.message : 'Unexpected error.'}
      </Callout>
    );
  }

  if (!data) return null;

  if (data.total_services === 0) {
    return (
      <div className="empty-state t-body">
        No services collected yet.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          Run <span className="mono">tools/collect-tenants.sh</span> on the host to populate.
        </div>
      </div>
    );
  }

  const isProblem = (s: PlatformService) =>
    s.state !== 'running' || s.health === 'unhealthy' || s.restart_count > RESTART_THRESHOLD;

  const shown = onlyProblems ? data.services.filter(isProblem) : data.services;

  return (
    <>
      <div className="kpi-row">
        <StatTile label="Services" value={data.total_services} />
        <StatTile label="Running" value={data.running_services} />
        <StatTile
          label="Problems"
          value={data.problem_services}
          tone={data.problem_services > 0 ? 'critical' : 'default'}
          sub={data.problem_services > 0 ? 'not running or restarting' : 'all healthy'}
        />
        <StatTile label="Stacks" value={data.stacks.length} />
      </div>

      {/* Observed, not live. Saying so is the difference between a dashboard and a guess. */}
      {data.is_stale && (
        <Callout tone="warning" title="This picture may be out of date">
          Last observed {data.observed_at ? formatWhen(data.observed_at) : 'unknown'} — the
          collector has not reported recently, so a service shown as running may have stopped since.
        </Callout>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} className="t-label">
          <input
            type="checkbox"
            checked={onlyProblems}
            onChange={(e) => setOnlyProblems(e.target.checked)}
          />
          Only problems
        </label>
        {data.observed_at && (
          <span className="t-label subtle">observed {formatWhen(data.observed_at)}</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {data.stacks.map((s) => (
          <div className="card stat-tile" key={s.stack}>
            <div className="stat-label t-label">{s.stack}</div>
            <div className="stat-value">{s.running}/{s.total}</div>
            <div className="stat-sub t-caption">
              {s.down > 0 ? `${s.down} down` : 'all up'}
              {s.unhealthy > 0 ? ` · ${s.unhealthy} unhealthy` : ''}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Containers</h2>
          <span className="t-label subtle">problems first</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Stack</th>
              <th>State</th>
              <th className="num">Restarts</th>
              <th>Uptime</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.container_name}>
                <td>
                  {s.service_name}
                  {s.health === 'unhealthy' && <span className="badge-muted">unhealthy</span>}
                </td>
                <td className="muted">{s.stack}</td>
                <td>
                  <span className={`chip ${s.state === 'running' ? 'chip-on' : 'chip-down'}`}>
                    {s.state}
                  </span>
                </td>
                <td
                  className="num"
                  style={{
                    // A high restart count is the finding — colour it so it is not read past.
                    color: s.restart_count > RESTART_THRESHOLD ? 'var(--viz-critical)' : undefined,
                    fontWeight: s.restart_count > RESTART_THRESHOLD ? 700 : undefined,
                  }}
                  title={s.restart_count > RESTART_THRESHOLD
                    ? 'Restarting repeatedly — reports "running" between restarts'
                    : undefined}
                >
                  {s.restart_count}
                </td>
                <td className="muted">{s.started_at ? formatWhen(s.started_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && (
          <div className="empty-state t-body">No problems — every container is running.</div>
        )}
      </div>
    </>
  );
}
